import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Mail, Bell, CheckCircle, Circle, Clock, FileText, ChevronRight, RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { ElencoAllegati } from '@/components/VisualizzatoreDocumento';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  AVVISO: { label: 'Avviso', color: 'bg-blue-100 text-blue-800', icon: Bell },
  URGENTE: { label: 'Urgente', color: 'bg-red-100 text-red-800', icon: Bell },
  LAVORI: { label: 'Lavori', color: 'bg-orange-100 text-orange-800', icon: FileText },
  SCADENZA: { label: 'Scadenza', color: 'bg-purple-100 text-purple-800', icon: Clock },
  ASSEMBLEA: { label: 'Assemblea', color: 'bg-green-100 text-green-800', icon: FileText },
  DOCUMENTO: { label: 'Documento', color: 'bg-cyan-100 text-cyan-800', icon: FileText },
  ALTRO: { label: 'Altro', color: 'bg-gray-100 text-gray-800', icon: Mail },
};

export default function MyCommunications() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedComm, setSelectedComm] = useState(null);

  const fetchCommunications = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/my-communications?page=${page}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCommunications(data.communications || []);
        setTotal(data.total || 0);
        setUnread(data.unread || 0);
        setPages(data.pages || 1);
      } else if (res.status === 400) {
        // Super admin doesn't have personal communications
        setCommunications([]);
        setTotal(0);
        setUnread(0);
      } else {
        toast.error('Errore nel caricamento delle comunicazioni');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore di connessione');
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => {
    fetchCommunications();
  }, [fetchCommunications]);

  const viewDetail = async (comm) => {
    try {
      const res = await fetch(`${API_URL}/api/my-communications/${comm.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSelectedComm(data);
        setShowDetailDialog(true);
        
        // Update local state to mark as read
        setCommunications(prev => prev.map(c => 
          c.id === comm.id ? { ...c, is_read: true } : c
        ));
        setUnread(prev => Math.max(0, prev - (comm.is_read ? 0 : 1)));
      }
    } catch (error) {
      toast.error('Errore nel caricamento');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Oggi';
    if (diffDays === 1) return 'Ieri';
    if (diffDays < 7) return `${diffDays} giorni fa`;
    
    return date.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short'
    });
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (user?.is_super_admin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <Mail className="h-12 w-12 text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold text-gray-600">Nessuna comunicazione</h2>
        <p className="text-gray-500">I super admin non hanno comunicazioni personali</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Le Mie Comunicazioni"
        subtitle={unread > 0 ? `${unread} da leggere` : 'Messaggi e avvisi ricevuti'}
        actions={(
          <Button variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20" onClick={fetchCommunications}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Aggiorna
          </Button>
        )}
      />

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-full">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{total}</div>
                <div className="text-sm text-gray-500">Totale</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-full">
                <Bell className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{unread}</div>
                <div className="text-sm text-gray-500">Da leggere</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Communications List */}
      <Card>
        <CardHeader>
          <CardTitle>Comunicazioni Ricevute</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : communications.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Nessuna comunicazione ricevuta</p>
            </div>
          ) : (
            <div className="divide-y">
              {communications.map(comm => {
                const typeCfg = TYPE_CONFIG[comm.comm_type] || TYPE_CONFIG.ALTRO;
                const TypeIcon = typeCfg.icon;
                
                return (
                  <div
                    key={comm.id}
                    onClick={() => viewDetail(comm)}
                    className={`py-4 px-2 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors ${
                      !comm.is_read ? 'bg-blue-50/50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Read indicator */}
                      <div className="pt-1">
                        {comm.is_read ? (
                          <CheckCircle className="h-4 w-4 text-gray-300" />
                        ) : (
                          <Circle className="h-4 w-4 text-blue-500 fill-blue-500" />
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={typeCfg.color} variant="secondary">
                            <TypeIcon className="h-3 w-3 mr-1" />
                            {typeCfg.label}
                          </Badge>
                          {comm.priority === 2 && (
                            <Badge variant="destructive">Urgente</Badge>
                          )}
                        </div>
                        <h3 className={`font-medium truncate ${!comm.is_read ? 'text-gray-900' : 'text-gray-700'}`}>
                          {comm.title}
                        </h3>
                        <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                          {comm.content}
                        </p>
                      </div>
                      
                      {/* Date & Arrow */}
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <span>{formatDate(comm.published_at)}</span>
                        <ChevronRight className="h-4 w-4" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Pagination */}
          {pages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                Precedente
              </Button>
              <span className="px-4 py-2 text-sm text-gray-500">
                Pagina {page} di {pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page === pages}
                onClick={() => setPage(p => Math.min(pages, p + 1))}
              >
                Successivo
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              {selectedComm && (
                <Badge className={TYPE_CONFIG[selectedComm.comm_type]?.color}>
                  {TYPE_CONFIG[selectedComm.comm_type]?.label}
                </Badge>
              )}
              {selectedComm?.protocol_number && (
                <span className="text-xs font-mono text-gray-400">
                  {selectedComm.protocol_number}
                </span>
              )}
            </div>
            <DialogTitle>{selectedComm?.title}</DialogTitle>
            <DialogDescription>
              {formatFullDate(selectedComm?.published_at)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedComm && (
            <div className="mt-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="whitespace-pre-wrap text-sm leading-relaxed">
                  {selectedComm.content}
                </p>
              </div>
              
              {/* Attachments */}
              {selectedComm.attachments?.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Allegati (solo consultazione)</h4>
                  <ElencoAllegati condominiumId={selectedComm.condominium_id} ids={selectedComm.attachments} />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowDetailDialog(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
